import React from "react";
import { useSelector } from "react-redux";
import { Box, Button, Stack, Typography } from "@mui/material";
import CookieOutlinedIcon from "@mui/icons-material/CookieOutlined";
import { useCookieConsent } from "../../context/CookieConsentContext";
import CookiePreferencesDialog from "./CookiePreferencesDialog";
import { colors, radii, shadows } from "../../theme/designTokens";

const DEFAULT_BANNER_TEXT =
  "Elifesaving Medicine uses cookies to enhance your browsing experience and improve our services. Learn more in our Cookie Policy.";

export default function CookieConsentBanner() {
  const { bannerVisible, preferencesOpen, acceptAll, rejectAll, openPreferences } =
    useCookieConsent();
  const { webSettings } = useSelector((state) => state.webSettings);
  const cookieSettings = (webSettings && webSettings.cookieConsent) || {};
  const enabled = cookieSettings.enabled !== false;

  return (
    <>
      {enabled && bannerVisible && !preferencesOpen && (
        <Box
          role="dialog"
          aria-live="polite"
          aria-label="Cookie consent"
          sx={{
            position: "fixed",
            left: { xs: 12, sm: 20 },
            right: { xs: 12, sm: "auto" },
            bottom: { xs: 12, sm: 20 },
            maxWidth: 360,
            zIndex: (theme) => theme.zIndex.modal + 1,
            bgcolor: colors.white,
            borderRadius: radii.lg,
            boxShadow: shadows.cardHover,
            border: `1px solid ${colors.line}`,
            p: 2.25,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: radii.md,
                bgcolor: colors.primaryLight,
                color: colors.primary,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <CookieOutlinedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontSize: 12.5, color: colors.ink2, lineHeight: 1.55, pt: 0.25 }}>
              {cookieSettings.bannerText || DEFAULT_BANNER_TEXT}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap", rowGap: 1 }}>
            <Button
              onClick={acceptAll}
              size="small"
              sx={{
                bgcolor: colors.accent,
                color: "#fff",
                textTransform: "none",
                fontWeight: 700,
                borderRadius: radii.md,
                px: 2,
                boxShadow: "none",
                "&:hover": { bgcolor: "#C94F21", boxShadow: "none" },
              }}
            >
              Accept All
            </Button>
            <Button
              onClick={rejectAll}
              size="small"
              sx={{
                color: colors.ink2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: colors.paperTint },
              }}
            >
              Reject
            </Button>
            <Button
              onClick={openPreferences}
              size="small"
              sx={{
                color: colors.primary,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: colors.primaryLight },
              }}
            >
              Settings
            </Button>
          </Stack>
        </Box>
      )}
      <CookiePreferencesDialog />
    </>
  );
}
